/**
 * csvDownload — client-side CSV export helper.
 *
 * Fetches a URL (with the caller's auth headers via axios), converts
 * the response body to a Blob, creates an object URL, triggers a
 * hidden `<a download>` click, and cleans up. Reusable for any future
 * admin export surface.
 *
 * Contract:
 *   - `endpoint` — absolute or relative URL. Relative URLs are
 *     resolved via `${REACT_APP_BACKEND_URL}/api${endpoint}`.
 *   - `filename` — optional client-side override for the download
 *     filename. If omitted, the browser falls back to the server's
 *     Content-Disposition header (which is the preferred path — the
 *     backend endpoint carries the date-stamped filename verbatim).
 *   - `params` — optional axios `params` object; forwarded verbatim.
 *
 * On failure: rethrows the underlying axios error so the caller can
 * surface a toast + reset any loading state. This helper does NOT
 * swallow errors — the intent is that the caller decides how loudly
 * to fail.
 *
 * Feb 2026 launch-blocking Contest A+B batch — Feature A csvExport.
 */
import { api } from "@/lib/api";

export async function downloadCsv(endpoint, { filename, params } = {}) {
  const resp = await api.get(endpoint, {
    responseType: "blob",
    params,
  });
  const blob = new Blob([resp.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  if (filename) {
    anchor.download = filename;
  }
  // Some browsers require the anchor to be in the DOM before click().
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Free the object URL on the next tick so the click has time to
  // resolve into a download in slower browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
